import AadeCaseSearchOrchestrator from "src/aadeCase/components/AadeCaseSearchOrchestrator";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TCaseFilter } from "src/aadeCase/data-access/types";
import useGetAdminBasicFilters from "src/aadeCase/hooks/useGetAdminBasicFilters";
import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import useGetAdminUnregisteredAadeCases from "src/aadeCase/data-access/useGetAdminUnregisteredAadeCases";
import { FieldValues } from "react-hook-form";
import { format, parseISO } from "date-fns";
import { Link } from "react-router-dom";
import i18n from "src/shared/services/languages/i18n";
import useCustomMaterialTable from "src/shared/hooks/useCustomMaterialTable";
import getTableLang from "src/shared/services/languages/MaterialReactTable/getTableLang";
import { MessageVariant } from "src/shared/enums/messageTypes/messageTypes";
import InfoMessage from "src/shared/components/InfoMessage";

export default function AdminUnregisteredAadeCases() {
  const { t } = useTranslation();
  const filters: TCaseFilter[] = useGetAdminBasicFilters();
  const [paginationState, setPaginationState] = useState<MRT_PaginationState>({
    pageSize: 10,
    pageIndex: 0,
  });
  const [enableSearch, setEnableSearch] = useState<Boolean>(true);
  const [searchFilters, setSearchFilters] = useState({});
  const {
    data: adminUnregisteredAadeCasesResponse,
    isLoading: isLoadingAdminUnregisteredAadeCases,
  } = useGetAdminUnregisteredAadeCases({
    searchFilters,
    enableSearch,
    setEnableSearch,
    paginationState,
  });

  const handleSearch = (data: FieldValues) => {
    setSearchFilters({
      ...data,
      date_from: data.date_from ? format(data.date_from, "yyyy-MM-dd") : null,
      date_to: data.date_to ? format(data.date_to, "yyyy-MM-dd") : null,
      legalType: data.legalType ? data.legalType.value : null,
      chamber: data.chamber ? data.chamber.value : null,
    });
    setEnableSearch(true);
  };

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        header: t("Αριθμός υπόθεσης"),
        accessorKey: "aade_case_id",
        accessorFn: (originalRow) => {
          return (
            <Link
              to={`${"/admin/manageAadeCaseApplication"}/${originalRow.aade_case_id}`}
            >
              {originalRow.aade_case_id}
            </Link>
          );
        },
      },
      {
        header: t("Αριθμός Γ.Ε.ΜΗ."),
        accessorKey: "company_id",
      },
      {
        header: t("ΑΦΜ"),
        accessorKey: "afm",
      },
      {
        header: t("Νομική Μορφή"),
        accessorKey: "legalType",
      },
      {
        header: t("Πλήθος αναντιστοιχιών"),
        accessorKey: "subcase_count",
      },
      {
        header: t("Ημερομηνία Δημιουργίας"),
        accessorKey: "date_created",
        accessorFn: (originalRow) => {
          return (
            <>
              {format(
                parseISO(originalRow.date_created),
                "dd/MM/yyyy HH:mm:ss",
              )}
            </>
          );
        },
      },
      {
        header: t("Επιμελητήριο"),
        accessorKey: "chamber",
      },
    ],
    [i18n.language],
  );

  const table = useCustomMaterialTable({
    columns,
    data: adminUnregisteredAadeCasesResponse?.data.rows ?? [],
    onPaginationChange: (updater) => {
      setPaginationState((previous) => updater(previous));
      setEnableSearch(true);
    },
    localization: getTableLang(),
    rowCount: adminUnregisteredAadeCasesResponse?.data.rowCount ?? 0,
    enableTopToolbar: false,
    enableColumnActions: false,
    enableSorting: false,
    state: {
      isLoading: isLoadingAdminUnregisteredAadeCases,
      showProgressBars: isLoadingAdminUnregisteredAadeCases,
      pagination: paginationState,
    },
    manualPagination: true,
  });

  return (
    <AadeCaseSearchOrchestrator
      title={t("Νέες εισερχόμενες Υποθέσεις Εκκαθάρισης")}
      infoMessageComponent={
        <InfoMessage
          variant={MessageVariant.Info}
          message={t(
            "Στην οθόνη αυτή εμφανίζονται υποθέσεις που έχουν δημιουργηθεί απο αναντιστοιχία στοιχείων της ΑΑΔΕ με το Γ.Ε.ΜΗ., απευθύνονται στην Υ. Γ.Ε.ΜΗ. και δεν έχουν ακόμη ανατεθεί σε υπάλληλο της Υ. Γ.Ε.ΜΗ. ώστε να αρχίσουν να διεκπεραιώνονται.",
          )}
        />
      }
      filters={filters}
      handleSearch={handleSearch}
      table={table}
    />
  );
}
